import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import axios from 'axios';
import { Package, Clock, CheckCircle, Truck, AlertCircle, ChevronRight, ShoppingBag, ArrowLeft, XCircle, Eye } from 'lucide-react';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { getPaymentDisplay } from '@/lib/paymentDisplay';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const statusConfig = {
  pending: { icon: Clock, color: 'text-yellow-600', bg: 'bg-yellow-50', label: 'Pending', textColor: 'text-yellow-700' },
  confirmed: { icon: CheckCircle, color: 'text-blue-600', bg: 'bg-blue-50', label: 'Confirmed', textColor: 'text-blue-700' },
  processing: { icon: Package, color: 'text-purple-600', bg: 'bg-purple-50', label: 'Processing', textColor: 'text-purple-700' },
  shipped: { icon: Truck, color: 'text-indigo-600', bg: 'bg-indigo-50', label: 'Shipped', textColor: 'text-indigo-700' },
  delivered: { icon: CheckCircle, color: 'text-green-600', bg: 'bg-green-50', label: 'Delivered', textColor: 'text-green-700' },
  cancelled: { icon: XCircle, color: 'text-red-600', bg: 'bg-red-50', label: 'Cancelled', textColor: 'text-red-700' }
};

const MyOrdersPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('all');

  const fetchOrders = async () => {
    try {
      const token = localStorage.getItem('session_token');
      const response = await axios.get(`${API}/customer/orders`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setOrders(response.data);
    } catch (err) {
      setError('Failed to load orders. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrders();
  }, []);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(price);
  };

  const getStatusInfo = (status) => {
    return statusConfig[status] || statusConfig.pending;
  };

  const filteredOrders = orders.filter(order => {
    if (filter === 'all') return true;
    if (filter === 'active') return !['delivered', 'cancelled'].includes(order.order_status);
    if (filter === 'completed') return order.order_status === 'delivered';
    if (filter === 'cancelled') return order.order_status === 'cancelled';
    return true;
  });

  const filterTabs = [
    { id: 'all', label: 'All' },
    { id: 'active', label: 'Active' },
    { id: 'completed', label: 'Completed' },
    { id: 'cancelled', label: 'Cancelled' }
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center pb-20">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-[#0047AB]"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 pb-20">
      <div className="max-w-3xl mx-auto px-4 py-6">
        {/* Header */}
        <div className="flex items-center gap-3 mb-6">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate(-1)}
            className="text-slate-600 -ml-2"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-xl font-bold text-slate-900">My Orders</h1>
            <p className="text-sm text-slate-500">
              {user?.name ? `Hi ${user.name.split(' ')[0]}, ` : ''}track your orders
            </p>
          </div>
        </div>

        {/* Filter Tabs */}
        <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
          {filterTabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setFilter(tab.id)}
              className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors ${
                filter === tab.id
                  ? 'bg-[#0047AB] text-white'
                  : 'bg-white text-slate-600 hover:bg-slate-100 border border-slate-200'
              }`}
              data-testid={`filter-${tab.id}`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {error ? (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
            {error}
          </div>
        ) : filteredOrders.length === 0 ? (
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-xl shadow-sm p-8 text-center"
          >
            <div className="w-20 h-20 mx-auto bg-slate-100 rounded-full flex items-center justify-center mb-4">
              <ShoppingBag className="w-10 h-10 text-slate-400" />
            </div>
            <h2 className="text-lg font-semibold text-slate-900 mb-2">
              {filter === 'all' ? 'No orders yet' : `No ${filter} orders`}
            </h2>
            <p className="text-slate-500 mb-6">
              {filter === 'all' ? 'Start shopping to see your orders here' : 'Check other tabs or start shopping'}
            </p>
            <Link 
              to="/products" 
              className="inline-flex items-center gap-2 bg-[#0047AB] text-white px-6 py-3 rounded-lg hover:bg-[#003380] transition-colors"
            >
              Browse Products
              <ChevronRight className="w-4 h-4" />
            </Link>
          </motion.div>
        ) : (
          <div className="space-y-3">
            {filteredOrders.map((order, index) => {
              const statusInfo = getStatusInfo(order.order_status);
              const StatusIcon = statusInfo.icon;
              const itemCount = order.items?.reduce((sum, item) => sum + item.quantity, 0) || 0;
              const firstItem = order.items?.[0];
              
              return (
                <motion.div
                  key={order.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                  onClick={() => navigate(`/order/${order.id}`)}
                  className="bg-white rounded-xl shadow-sm overflow-hidden cursor-pointer hover:shadow-md transition-shadow active:scale-[0.99]"
                  data-testid={`order-card-${order.id}`}
                >
                  <div className="p-4">
                    {/* Order Header */}
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${statusInfo.bg} ${statusInfo.textColor}`}>
                          <StatusIcon className="w-3.5 h-3.5" />
                          {statusInfo.label}
                        </span>
                        <span className="text-xs text-slate-400">#{order.id}</span>
                      </div>
                      <ChevronRight className="w-5 h-5 text-slate-400" />
                    </div>

                    {/* Order Content */}
                    <div className="flex gap-3">
                      {/* Product Image */}
                      <div className="w-16 h-16 bg-slate-100 rounded-lg flex items-center justify-center flex-shrink-0 overflow-hidden">
                        {firstItem?.image_url ? (
                          <img src={firstItem.image_url} alt="" className="w-full h-full object-cover" />
                        ) : (
                          <Package className="w-6 h-6 text-slate-400" />
                        )}
                      </div>
                      
                      {/* Order Details */}
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-slate-900 truncate">
                          {firstItem?.product_name || 'Order'}
                          {itemCount > 1 && <span className="text-slate-500"> +{itemCount - 1} more</span>}
                        </p>
                        <p className="text-xs text-slate-500 mt-0.5">
                          {formatDate(order.created_at)}
                        </p>
                        <div className="flex items-center justify-between mt-2">
                          <p className="font-semibold text-[#0047AB]">{formatPrice(order.total_amount)}</p>
                          <span className={`text-xs font-medium ${getPaymentDisplay(order).color}`} data-testid="payment-status-text">
                            {getPaymentDisplay(order).label}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Track Button */}
                  {!['delivered', 'cancelled'].includes(order.order_status) && (
                    <div className="bg-slate-50 px-4 py-2.5 border-t border-slate-100">
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-slate-500">
                          Expected by {(() => {
                            const d = new Date(order.created_at);
                            d.setDate(d.getDate() + 7);
                            return d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
                          })()}
                        </span>
                        <span className="text-[#0047AB] font-medium flex items-center gap-1">
                          <Eye className="w-4 h-4" />
                          Track Order
                        </span>
                      </div>
                    </div>
                  )}
                </motion.div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default MyOrdersPage;
